import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Grid, List, Clock, AlertCircle, FileText, Settings, Trash2, Edit, Eye } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { formatDate } from '../../utils/formatters';
import { API_URL } from '../../utils/config';
import { apiClient } from '../../api/client';

interface Template {
  id: string;
  name: string;
  description: string;
  status: 'draft' | 'active' | 'archived';
  lastUsed: string;
  version: string;
  createdAt: string;
  documentTypes: string[];
}

const TemplatePage = () => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/api/templates');
      setTemplates(response.data);
    } catch (error: unknown) {
      let errorMessage = 'Failed to fetch templates';
      
      if (error && typeof error === 'object') {
        if ('response' in error && error.response && typeof error.response === 'object') {
          const axiosError = error as { response: { data?: { detail?: string } } };
          errorMessage = axiosError.response.data?.detail || errorMessage;
        } else if ('message' in error && typeof error.message === 'string') {
          errorMessage = error.message;
        }
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTemplate = () => {
    navigate('/templates/new');
  };

  const handleEditTemplate = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`/templates/${id}/edit`);
  };

  const handleViewTemplate = (id: string) => {
    navigate(`/templates/${id}`);
  };

  const handleDeleteTemplate = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (deleteConfirm === id) {
      try {
        await apiClient.delete(`/api/templates/${id}`);
        setTemplates(templates.filter(t => t.id !== id));
        setDeleteConfirm(null);
      } catch (error: unknown) {
        let errorMessage = 'Failed to delete template';
        
        if (error && typeof error === 'object') {
          if ('response' in error && error.response && typeof error.response === 'object') {
            const axiosError = error as { response: { data?: { detail?: string } } };
            errorMessage = axiosError.response.data?.detail || errorMessage;
          } else if ('message' in error && typeof error.message === 'string') {
            errorMessage = error.message;
          }
        }
        
        setError(errorMessage);
      }
    } else {
      setDeleteConfirm(id);
      // Reset confirmation after 3 seconds
      setTimeout(() => setDeleteConfirm(null), 3000);
    }
  };

  const filteredTemplates = templates.filter(template =>
    template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    template.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const renderTemplateCard = (template: Template) => (
    <Card 
      key={template.id}
      className="hover:bg-purple-500/10 transition-all duration-200 cursor-pointer"
      onClick={() => handleViewTemplate(template.id)}
    >
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-purple-500/20">
              <FileText className="w-6 h-6 text-purple-400" />
            </div>
            <div>
              <CardTitle className="text-base text-purple-100 truncate">
                {template.name}
              </CardTitle>
              <p className="text-sm text-purple-400/60 truncate">
                {template.description}
              </p>
            </div>
          </div>
          <div className={`px-2 py-1 rounded-full text-xs 
            ${template.status === 'active' 
              ? 'bg-green-500/20 text-green-300' 
              : template.status === 'draft'
              ? 'bg-yellow-500/20 text-yellow-300'
              : 'bg-purple-500/20 text-purple-300'
            }`}>
            {template.status}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="mt-2 flex items-center gap-4">
          <div className="flex items-center gap-2 text-purple-400/60">
            <Clock className="w-4 h-4" />
            <span className="text-xs">Last used {formatDate(template.lastUsed)}</span>
          </div>
          <div className="flex-1" />
          <div className="flex items-center gap-2">
            <button
              onClick={(e) => handleEditTemplate(template.id, e)}
              className="p-1 text-purple-400 hover:text-purple-300 hover:bg-purple-500/20 rounded"
            >
              <Edit className="w-4 h-4" />
            </button>
            <button
              onClick={(e) => handleDeleteTemplate(template.id, e)}
              className={`p-1 ${
                deleteConfirm === template.id
                  ? 'text-red-400 bg-red-500/20'
                  : 'text-purple-400 hover:text-purple-300 hover:bg-purple-500/20'
              } rounded`}
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-white">Templates</h1>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-lg ${
                viewMode === 'grid' ? 'bg-purple-500/20' : 'hover:bg-purple-500/10'
              }`}
            >
              <Grid className="w-5 h-5 text-purple-300" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded-lg ${
                viewMode === 'list' ? 'bg-purple-500/20' : 'hover:bg-purple-500/10'
              }`}
            >
              <List className="w-5 h-5 text-purple-300" />
            </button>
          </div>
          <button
            onClick={handleCreateTemplate}
            className="flex items-center gap-2 px-4 py-2 bg-purple-500 hover:bg-purple-600 rounded-lg text-white font-medium transition-colors"
          >
            <Plus className="w-4 h-4" />
            New Template
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-purple-400/60 w-5 h-5" />
          <input
            type="text"
            placeholder="Search templates..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-purple-500/10 border border-purple-500/20 rounded-lg text-purple-100 placeholder-purple-400/60 focus:outline-none focus:ring-2 focus:ring-purple-500/40"
          />
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-purple-500/20 border-t-purple-500 rounded-full animate-spin" />
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="flex items-center gap-2 p-4 bg-red-500/10 border border-red-500/20 rounded-lg text-red-200">
          <AlertCircle className="w-5 h-5" />
          <p>{error}</p>
        </div>
      )}

      {/* Empty State */}
      {!loading && !error && filteredTemplates.length === 0 && (
        <div className="text-center py-12">
          <Settings className="w-12 h-12 text-purple-400/40 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-purple-100">No Templates Found</h3>
          <p className="text-purple-400/60 mt-1">
            {searchTerm ? 'Try adjusting your search terms' : 'Create your first template to get started'}
          </p>
          <button
            onClick={handleCreateTemplate}
            className="mt-4 px-4 py-2 bg-purple-500/20 hover:bg-purple-500/30 rounded-lg text-purple-300 font-medium transition-colors"
          >
            Create Template
          </button>
        </div>
      )}

      {/* Template Grid */}
      {!loading && !error && filteredTemplates.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredTemplates.map(renderTemplateCard)}
        </div>
      )}
    </div>
  );
};

export default TemplatePage;